<script setup>
/**
 * The notifications section of the settings page (see src/push/usePush.js).
 *
 * It renders nothing when the feature is switched off on the server: the settings page does not
 * have to know that it exists. It has no save button of its own either - the page has one for the
 * whole form and calls `save()` through the template reference.
 */
import { onMounted } from 'vue'
import { usePush } from '@/push/usePush'

const {
  available,
  loading,
  saving,
  activating,
  supported,
  insecure,
  blocked,
  serviceWorkerIssue,
  deviceLost,
  channels,
  rows,
  preferences,
  subscriptions,
  intervalItems,
  perDayItems,
  thresholdItems,
  thisDeviceActive,
  needsHomeScreen,
  isDesktop,
  emailVerified,
  deviceLabel,
  settingsUrl,
  canShare,
  load,
  activate,
  removeSubscription,
  save,
  sendTest,
  copyLink,
  shareLink,
} = usePush()

onMounted(load)

// What the single save button of the settings page calls. `saving` keeps the table locked while
// the request is in flight; the outcome (and the message) belong to the page.
defineExpose({ save })
</script>

<template>
  <v-card v-if="available && !loading" elevation="2">
    <v-card-title class="d-flex align-center">
      <v-icon color="primary" class="mr-3">mdi-bell-ring-outline</v-icon>
      {{ $t('push.title') }}
    </v-card-title>
    <v-card-subtitle>{{ $t('push.subtitle') }}</v-card-subtitle>

    <v-card-text>
      <!-- The origin has to be secure: over http, or behind a certificate the browser does not
           trust, there is no Push API at all and the button can only fail. -->
      <v-alert v-if="insecure" type="warning" variant="tonal" density="comfortable">
        {{ $t('push.insecure') }}
      </v-alert>

      <!-- The browser has the notifications blocked for this site: everything else can be right
           and nothing will arrive. The server cannot detect it (it is told the message was
           accepted), so this warning is the only place where it can be said. -->
      <v-alert v-else-if="blocked" type="warning" variant="tonal" density="comfortable">
        {{ $t('push.blocked') }}
      </v-alert>

      <!-- The file of the service worker did not load: the probe in usePush.js says why. -->
      <v-alert v-else-if="serviceWorkerIssue" type="warning" variant="tonal" density="comfortable">
        {{ $t(serviceWorkerIssue === 'blocked' ? 'push.swBlocked' : 'push.swMissing') }}
      </v-alert>

      <v-alert v-else-if="!supported" type="info" variant="tonal" density="comfortable">
        {{ $t('push.unsupported') }}
      </v-alert>

      <template v-else>
        <!-- This device: the only channel that can be activated from here -->
        <div class="d-flex align-center flex-wrap ga-4 mb-6">
          <div>
            <div class="text-body-2 font-weight-medium">{{ $t('push.thisDevice') }}</div>
            <div class="text-caption text-medium-emphasis">{{ deviceLabel }}</div>
          </div>

          <v-chip v-if="thisDeviceActive" color="success" prepend-icon="mdi-check-circle-outline">
            {{ $t('push.active') }}
          </v-chip>
          <v-btn
            v-else
            color="primary"
            variant="tonal"
            prepend-icon="mdi-bell-plus-outline"
            :loading="activating"
            :disabled="needsHomeScreen || insecure || blocked"
            @click="activate"
          >
            {{ $t('push.activate') }}
          </v-btn>
        </div>

        <!-- This device had a subscription and lost it: the permission was blocked, the browser
             cleared its data, or the row was removed from the list. Nothing arrives here and
             nobody notices, so it is said. -->
        <v-alert v-if="deviceLost" type="warning" variant="tonal" density="comfortable" class="mb-6">
          {{ $t('push.deviceLost') }}
        </v-alert>

        <!-- iPhone and iPad: in a browser tab there is nothing to subscribe -->
        <v-alert v-if="needsHomeScreen" type="info" variant="tonal" density="comfortable" class="mb-6">
          <div class="font-weight-medium mb-1">{{ $t('push.ios.title') }}</div>
          {{ $t('push.ios.instructions') }}
        </v-alert>

        <!-- From the PC: the phone has to open this very page and press the same button -->
        <div v-else-if="isDesktop" class="mb-6">
          <div class="text-body-2 font-weight-medium mb-1">{{ $t('push.phone.title') }}</div>
          <div class="text-body-2 mb-2">{{ $t('push.phone.instructions') }}</div>

          <!-- The address itself copies too: it is the first thing one tries to click. `link` is
               what makes the chip answer the pointer (and the keyboard) like a button. -->
          <v-tooltip :text="$t('push.phone.copy')" location="top">
            <template #activator="{ props }">
              <v-chip v-bind="props" link class="mb-2" variant="tonal" label @click="copyLink">
                <v-icon start>mdi-cellphone-link</v-icon>
                {{ settingsUrl }}
              </v-chip>
            </template>
          </v-tooltip>

          <div class="d-flex flex-wrap ga-2">
            <v-btn size="small" variant="tonal" prepend-icon="mdi-content-copy" @click="copyLink">
              {{ $t('push.phone.copy') }}
            </v-btn>
            <v-btn
              v-if="canShare"
              size="small"
              variant="tonal"
              prepend-icon="mdi-share-variant"
              @click="shareLink"
            >
              {{ $t('push.phone.share') }}
            </v-btn>
          </div>
        </div>

        <v-divider class="my-6" />

        <!-- The table: the conditions are the rows, the channels are the columns. The channel
             columns have a fixed width so the header and the checkbox of a channel line up, and
             the checkbox sits in a flex box because the control itself is not an inline element. -->
        <v-table density="comfortable">
          <thead>
            <tr>
              <th class="text-start">{{ $t('push.table.notify') }}</th>
              <th
                v-for="channel in channels"
                :key="channel"
                class="text-center"
                style="width: 110px"
              >
                {{ $t('push.channels.' + channel) }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in rows" :key="row.conditionKey">
              <td class="py-3">
                <div class="text-body-2 mb-1">{{ $t('push.conditions.' + row.conditionKey) }}</div>
                <v-select
                  v-model="row.threshold"
                  :items="thresholdItems(row.thresholds)"
                  :label="$t('push.table.threshold')"
                  density="compact"
                  variant="outlined"
                  hide-details
                  :disabled="saving"
                  class="mt-2"
                  style="max-width: 150px"
                />
              </td>
              <td v-for="channel in channels" :key="channel" class="text-center py-3">
                <div class="d-flex justify-center">
                  <v-checkbox-btn v-model="row.channels[channel]" :disabled="saving" />
                </div>
              </td>
            </tr>
          </tbody>
        </v-table>

        <div class="text-caption text-medium-emphasis mt-3">{{ $t('push.offlineNote') }}</div>
        <div v-if="!emailVerified" class="text-caption text-warning mt-2">
          {{ $t('push.unverifiedEmail') }}
        </div>

        <v-divider class="my-6" />

        <!-- How often the user accepts to be contacted -->
        <div class="text-body-2 font-weight-medium">{{ $t('push.frequency.title') }}</div>
        <div class="text-caption text-medium-emphasis mb-3">{{ $t('push.frequency.subtitle') }}</div>

        <v-row>
          <v-col cols="12" md="6">
            <v-select
              v-model="preferences.minIntervalMinutes"
              :items="intervalItems"
              :label="$t('push.frequency.minInterval')"
              variant="outlined"
              density="comfortable"
              :disabled="saving"
            />
          </v-col>
          <v-col cols="12" md="6">
            <v-select
              v-model="preferences.maxPerDay"
              :items="perDayItems"
              :label="$t('push.frequency.maxPerDay')"
              variant="outlined"
              density="comfortable"
              :disabled="saving"
            />
          </v-col>
        </v-row>

        <v-divider class="my-6" />

        <!-- The devices that are subscribed, and the way to check one of them -->
        <div class="text-body-2 font-weight-medium mb-2">{{ $t('push.devices.title') }}</div>

        <div v-if="!subscriptions.length" class="text-body-2 text-medium-emphasis mb-4">
          {{ $t('push.devices.none') }}
        </div>
        <div v-else class="d-flex flex-wrap ga-2 mb-4">
          <v-chip
            v-for="device in subscriptions"
            :key="device.id"
            closable
            @click:close="removeSubscription(device.id)"
          >
            {{ device.label || $t('push.devices.unknown') }}
          </v-chip>
        </div>

        <div class="text-body-2 font-weight-medium mb-2">{{ $t('push.test.title') }}</div>
        <div class="d-flex flex-wrap ga-2">
          <v-btn
            v-for="channel in channels"
            :key="channel"
            size="small"
            variant="tonal"
            prepend-icon="mdi-send-outline"
            @click="sendTest(channel)"
          >
            {{ $t('push.channels.' + channel) }}
          </v-btn>
        </div>
      </template>
    </v-card-text>
  </v-card>
</template>
